import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useGetClassifiersListQuery } from 'store/api/classifiers';
import { ClassifierData } from 'types/classifiers';

interface UseClassifiersProps {
    codes: string[];
    autoSort?: boolean;
}

/**
 * Custom hook for fetching and managing classifier data.
 *
 * @param codes - Array of classifier codes to fetch. These codes identify specific
 *                classifiers from the available list (e.g., 'RELATIONSHIP_TYPE',
 *                'PERSON_TITLE', 'COUNTRY', etc.)
 * @param autoSort - Whether to automatically sort the classifiers by orderNum value from the DB. Defaults to false.
 *
 * @returns {Object} Object containing:
 *   - classifiers: Record<string, ClassifierData> - Object with classifier codes as keys and classifier data as values
 *   - classifierArray: ClassifierData[] - Array of classifiers filtered by the provided codes
 *   - allClassifiers: ClassifierData[] - Array of all available classifiers from the API
 *   - isLoading: boolean - Loading state for the classifiers data
 */

export const useClassifiers = ({ codes, autoSort = false }: UseClassifiersProps) => {
    const { data: classifiers, isLoading } = useGetClassifiersListQuery();

    const codesStringify = useMemo(() => JSON.stringify(codes), [codes]);

    // Derived rather than mirrored into state via an effect: an effect that bails out on an absent
    // `classifiers` can never clear its own loading flag, so a failed request would leave every
    // consumer gated on `isLoading` waiting forever. Deriving keeps `isLoading` the query's alone.
    const classifierObject = useMemo(() => {
        const collectedClassifiers = {} as Record<string, ClassifierData>;
        classifiers?.forEach((classifier) => {
            if (codes.includes(classifier.code)) {
                const classifierCopy = { ...classifier };

                // Sort values by orderNum if autoSort is true
                if (autoSort && classifierCopy.values && classifierCopy.values.length > 0) {
                    classifierCopy.values = classifierCopy.values.toSorted((a, b) => {
                        // Handle cases where orderNum might be undefined
                        const orderA = a.orderNum ?? 0;
                        const orderB = b.orderNum ?? 0;
                        return orderA - orderB;
                    });
                }

                collectedClassifiers[classifier.code] = classifierCopy;
            }
        });

        return collectedClassifiers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classifiers, codesStringify, autoSort]);

    const filteredClassifiers = useMemo(
        () => classifiers?.filter((item) => codes.includes(item.code)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [classifiers, codesStringify]
    );
    return {
        classifiers: classifierObject,
        classifierArray: filteredClassifiers ?? [],
        allClassifiers: classifiers ?? [],
        isLoading,
    };
};

export const useValidClassifierOptions = <TOption extends FieldValues>(
    options: TOption[],
    initialWatchedValue: TOption[] | TOption
) => {
    return useMemo(() => {
        const descriptionsSet = new Set(options.map((o) => o.description));

        const mergedOptions = [...options];
        const arrayWatchedValue = Array.isArray(initialWatchedValue) ? initialWatchedValue : [initialWatchedValue];

        for (const watchedValue of arrayWatchedValue) {
            const wvOption = watchedValue as TOption;
            if (wvOption && !descriptionsSet.has(wvOption.description)) {
                mergedOptions.push(wvOption);
                descriptionsSet.add(wvOption.description);
            }
        }

        return mergedOptions;
    }, [options, initialWatchedValue]);
};
